import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../../components/UI/Button';
import { Modal } from '../../components/UI/Modal';
import { ImportModal } from '../../components/Import/ImportModal';
import { teamService } from '../../services/teamService';
import { Team, TeamWithMemberCount, CreateTeamRequest, UpdateTeamRequest } from '../../types/teams';
import { useToast } from '../../components/UI/ToastProvider';
import { IMPORT_TEAMS_COMPLETED_EVENT } from '../../utils/importNotificationEvents';

export const TeamsPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [teams, setTeams] = useState<TeamWithMemberCount[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState<Team | null>(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const { addToast } = useToast();

  // Form state
  const [form, setForm] = useState<{
    name: string;
    description: string;
    isActive: boolean;
  }>({
    name: '',
    description: '',
    isActive: true
  });

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    setLoading(true);
    try {
      const data = await teamService.getTeamsWithMemberCount();
      setTeams(data);
    } catch (error: any) {
      console.error('Erro ao buscar equipes:', error);
      addToast('Erro ao carregar equipes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamsRef = useRef(fetchTeams);
  fetchTeamsRef.current = fetchTeams;

  useEffect(() => {
    const handleImportCompleted = () => {
      void fetchTeamsRef.current();
    };

    window.addEventListener(IMPORT_TEAMS_COMPLETED_EVENT, handleImportCompleted);
    return () => {
      window.removeEventListener(IMPORT_TEAMS_COMPLETED_EVENT, handleImportCompleted);
    };
  }, []);

  const filteredTeams = (teams || []).filter(team =>
    team.name.toLowerCase().includes(search.toLowerCase()) ||
    team.description.toLowerCase().includes(search.toLowerCase())
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleFormChange = (field: string, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const openCreateModal = () => {
    setIsEditMode(false);
    setEditingTeam(null);
    setForm({
      name: '',
      description: '',
      isActive: true
    });
    setIsModalOpen(true);
  };

  const openEditModal = (team: Team) => {
    setIsEditMode(true);
    setEditingTeam(team);
    setForm({
      name: team.name,
      description: team.description,
      isActive: team.isActive,
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setModalLoading(true);

    // Validação dos campos obrigatórios
    if (!form.name.trim()) {
      addToast('Nome da equipe é obrigatório', 'error');
      setModalLoading(false);
      return;
    }

    if (!form.description.trim()) {
      addToast('Descrição da equipe é obrigatória', 'error');
      setModalLoading(false);
      return;
    }

    try {
      if (isEditMode && editingTeam) {
        const updateData: UpdateTeamRequest = {
          id: editingTeam.id,
          name: form.name,
          description: form.description,
          isActive: form.isActive,
        };

        await teamService.updateTeam(editingTeam.id, updateData);
        addToast('Equipe atualizada com sucesso', 'success');
      } else {
        const createData: CreateTeamRequest = {
          name: form.name,
          description: form.description,
          isActive: form.isActive,
        };

        await teamService.createTeam(createData);
        addToast('Equipe criada com sucesso', 'success');
      }

      setIsModalOpen(false);
      fetchTeams();
    } catch (error: any) {
      console.error('Erro ao salvar equipe:', error);
      const message = error.response?.data?.message || 'Erro ao salvar equipe';
      addToast(message, 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDelete = async (team: Team) => {
    setTeamToDelete(team);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!teamToDelete) return;

    try {
      await teamService.deleteTeam(teamToDelete.id);
      addToast('Equipe excluída com sucesso', 'success');
      fetchTeams();
    } catch (error: any) {
      console.error('Erro ao excluir equipe:', error);
      const message = error.response?.data?.message || 'Erro ao excluir equipe';
      addToast(message, 'error');
    } finally {
      setIsDeleteModalOpen(false);
      setTeamToDelete(null);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getTeamNames = (teamIds: string[]) => {
    if (!teamIds || !Array.isArray(teamIds)) return '';
    return teamIds.map(id => {
      const team = teams?.find(t => t.id === id);
      return team?.name || 'Equipe não encontrada';
    }).join(', ');
  };

  return (
    <div className="flex-1">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Gerenciamento de Equipes</h1>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setIsImportModalOpen(true)}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Importar
          </Button>
          <Button onClick={openCreateModal}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nova Equipe
          </Button>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-4 flex justify-between items-center">
          <div className="relative">
            <input
              type="text"
              placeholder="Buscar equipes..."
              className="border rounded-md pl-10 pr-4 py-2 w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={search}
              onChange={handleSearch}
            />
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Carregando equipes...</p>
            </div>
          ) : filteredTeams.length === 0 ? (
            <div className="text-center py-12">
              <div className="mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {search ? 'Nenhuma equipe encontrada' : 'Nenhuma equipe cadastrada'}
              </h3>
              <p className="text-gray-500 mb-6">
                {search 
                  ? 'Tente ajustar os termos de busca ou criar uma nova equipe.'
                  : 'Comece criando sua primeira equipe para organizar os usuários.'
                }
              </p>
              {!search && (
                <Button onClick={openCreateModal}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Criar primeira equipe
                </Button>
              )}
            </div>
          ) : (
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-100">
                  <th className="py-3 px-4 text-left text-gray-600 font-medium">Nome</th>
                  <th className="py-3 px-4 text-left text-gray-600 font-medium">Descrição</th>
                  <th className="py-3 px-4 text-left text-gray-600 font-medium">Status</th>
                  <th className="py-3 px-4 text-left text-gray-600 font-medium">Membros</th>
                  <th className="py-3 px-4 text-left text-gray-600 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredTeams.map(team => (
                  <tr key={team.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">{team.name || 'Nome não informado'}</td>
                    <td className="py-3 px-4 text-gray-600">{team.description || 'Descrição não informada'}</td>
                    <td className="py-3 px-4">
                      <span className={
                        team.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-200 text-gray-700'
                      + ' text-xs font-medium px-3 py-1 rounded-full'}>
                        {team.isActive ? 'Ativa' : 'Inativa'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {team.memberCount} membros
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex space-x-2">
                        <Button 
                          variant="secondary" 
                          size="sm"
                          onClick={() => openEditModal({
                            id: team.id,
                            name: team.name,
                            description: team.description,
                            members: [], // Campo não usado na edição
                            leader: team.leader,
                            createdAt: team.createdAt,
                            updatedAt: team.updatedAt,
                            isActive: team.isActive
                          })}
                          title="Editar equipe"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </Button>
                        <Button 
                          variant="danger" 
                          size="sm"
                          onClick={() => handleDelete({
                            id: team.id,
                            name: team.name,
                            description: team.description,
                            members: [], // Campo não usado na exclusão
                            leader: team.leader,
                            createdAt: team.createdAt,
                            updatedAt: team.updatedAt,
                            isActive: team.isActive
                          })}
                          title="Excluir equipe"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Modal 
        isOpen={isModalOpen} 
        title={isEditMode ? "Editar Equipe" : "Nova Equipe"} 
        onClose={() => setIsModalOpen(false)}
      >
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
            <input 
              type="text" 
              className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={form.name}
              onChange={e => handleFormChange('name', e.target.value)}
              required
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
            <textarea 
              className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              value={form.description}
              onChange={e => handleFormChange('description', e.target.value)}
              required
            />
          </div>
          
          <div className="mb-4">
            <label className="flex items-center">
              <input 
                type="checkbox" 
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={form.isActive}
                onChange={e => handleFormChange('isActive', e.target.checked)}
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Equipe ativa</span>
            </label>
          </div>
          
          <div className="flex justify-end space-x-3">
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => setIsModalOpen(false)}
              disabled={modalLoading}
            >
              Cancelar
            </Button>
            <Button 
              type="submit" 
              disabled={modalLoading}
            >
              {modalLoading ? (
                <>
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Salvando...
                </>
              ) : (
                'Salvar'
              )}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        title="Confirmar Exclusão"
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <p className="text-gray-700 mb-4">
          Tem certeza que deseja excluir a equipe "{teamToDelete?.name}"? Esta ação não pode ser desfeita.
        </p>
        <div className="flex justify-end space-x-3">
          <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Excluir
          </Button>
        </div>
      </Modal>

      <ImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        entityType="teams"
      />
    </div>
  );
}; 